import { api, unwrap } from "@lumberset/api-client";
import {
  Button,
  Container,
  Drawer,
  EmptyState,
  Field,
  Input,
  SectionHeader,
  Table,
  Textarea,
  toast,
} from "@lumberset/ui";
import { MagnifyingGlass, Plus, Users } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useDeferredValue, useState } from "react";
import { Crumbs } from "../../components/crumbs";
import { customersQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/customers")({ component: Customers });

type Customer = Awaited<
  ReturnType<NonNullable<ReturnType<typeof customersQuery>["queryFn"]>>
>["customers"][number];

function Customers() {
  const [text, setText] = useState("");
  const q = useDeferredValue(text);
  const { data } = useQuery(customersQuery(q || undefined));
  const [editing, setEditing] = useState<Customer | "new" | null>(null);

  return (
    <>
      <Crumbs trail={[{ label: "Customers" }]} />
      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Customers"
          description="The builders and contractors your yard quotes."
          actions={
            <Button size="small" onClick={() => setEditing("new")}>
              <Plus /> New customer
            </Button>
          }
        />
        <div className="px-6 py-3 sm:max-w-xs">
          <Input
            type="search"
            placeholder="Search customers"
            prefix={<MagnifyingGlass />}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        </div>

        {data && data.customers.length === 0 ? (
          <EmptyState
            icon={<Users />}
            title={q ? "No customers match" : "No customers yet"}
            description={
              q
                ? "Try a different name."
                : "Add the builders you quote, so a quote can be sent in one click."
            }
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell className="max-md:hidden">Company</Table.HeaderCell>
                <Table.HeaderCell className="max-sm:hidden">Email</Table.HeaderCell>
                <Table.HeaderCell className="max-lg:hidden">Phone</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data?.customers.map((customer) => (
                <Table.Row
                  key={customer.id}
                  data-clickable="true"
                  onClick={() => setEditing(customer)}
                >
                  <Table.Cell className="txt-compact-small-plus text-ui-fg-base">
                    {customer.name}
                  </Table.Cell>
                  <Table.Cell className="max-md:hidden">{customer.company ?? "-"}</Table.Cell>
                  <Table.Cell className="max-sm:hidden">{customer.email ?? "-"}</Table.Cell>
                  <Table.Cell className="max-lg:hidden">{customer.phone ?? "-"}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <CustomerDrawer editing={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function CustomerDrawer({
  editing,
  onClose,
}: {
  editing: Customer | "new" | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const existing = editing !== "new" ? editing : null;

  const save = useMutation({
    mutationFn: (form: FormData) => {
      const text = (name: string) => String(form.get(name) ?? "").trim() || null;
      const json = {
        name: String(form.get("name")).trim(),
        company: text("company"),
        email: text("email"),
        phone: text("phone"),
        notes: text("notes"),
      };
      return existing
        ? unwrap(api.customers[":id"].$patch({ param: { id: existing.id }, json }))
        : unwrap(api.customers.$post({ json }));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success(existing ? "Customer saved" : "Customer added");
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(new FormData(event.currentTarget));
  }

  return (
    <Drawer open={editing !== null} onOpenChange={(open) => !open && onClose()}>
      <Drawer.Content>
        <form
          onSubmit={submit}
          className="flex min-h-0 flex-1 flex-col"
          key={existing?.id ?? "new"}
        >
          <Drawer.Header>
            <Drawer.Title>{existing ? existing.name : "New customer"}</Drawer.Title>
            <Drawer.Description>Quotes are emailed to this address.</Drawer.Description>
          </Drawer.Header>
          <Drawer.Body className="flex flex-col gap-y-5">
            <Field label="Name">
              {(control) => (
                <Input {...control} name="name" defaultValue={existing?.name} required autoFocus />
              )}
            </Field>
            <Field label="Company" optional>
              {(control) => (
                <Input {...control} name="company" defaultValue={existing?.company ?? ""} />
              )}
            </Field>
            <Field label="Email" optional>
              {(control) => (
                <Input
                  {...control}
                  name="email"
                  type="email"
                  defaultValue={existing?.email ?? ""}
                />
              )}
            </Field>
            <Field label="Phone" optional>
              {(control) => (
                <Input {...control} name="phone" type="tel" defaultValue={existing?.phone ?? ""} />
              )}
            </Field>
            <Field label="Notes" optional>
              {(control) => (
                <Textarea {...control} name="notes" defaultValue={existing?.notes ?? ""} />
              )}
            </Field>
          </Drawer.Body>
          <Drawer.Footer>
            <Button type="button" variant="secondary" size="small" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="small" isLoading={save.isPending}>
              Save
            </Button>
          </Drawer.Footer>
        </form>
      </Drawer.Content>
    </Drawer>
  );
}
